import { useMemo } from 'react';

import type { CustomFieldWithValue } from '@/types/ui';

import {
  type CustomFieldFormValues,
  isCustomFieldRequired,
} from '@/types/schemas';

export type SerializedCustomFieldValue = {
  company_custom_field_id: number;
  value: string;
};

export type CustomFieldState = CustomFieldWithValue & {
  current_value: string | null | boolean;
  isRequired: boolean;
};

/**
 * Hook for working with company custom fields in forms
 * Provides field metadata, initial values, and serialization utilities
 */
export const useCompanyCustomFields = (
  customFields: CustomFieldWithValue[],
): {
  fields: CustomFieldState[];
  getInitialValues: () => CustomFieldFormValues;
  serializeForSubmit: (
    formValues: CustomFieldFormValues,
  ) => SerializedCustomFieldValue[];
} => {
  const fields = useMemo<CustomFieldState[]>(() => {
    return (customFields || []).map((field) => {
      const savedValue = field.client_value;

      let initialValue: string | null | boolean;

      switch (field.field_type) {
        case 'checkbox':
          initialValue = savedValue === 'true';
          break;
        default:
          initialValue = typeof savedValue === 'string' ? savedValue : null;
          break;
      }

      return {
        ...field,
        current_value: initialValue,
        isRequired: isCustomFieldRequired(field),
      };
    });
  }, [customFields]);

  const getInitialValues = (): CustomFieldFormValues => {
    return fields.reduce((acc, field) => {
      return {
        ...acc,
        [field.id]: field.current_value,
      };
    }, {} as CustomFieldFormValues);
  };

  const serializeForSubmit = (
    formValues: CustomFieldFormValues,
  ): SerializedCustomFieldValue[] => {
    return fields
      .map((field) => {
        const rawValue = formValues[field.id];
        let value: string;

        if (field.field_type === 'checkbox') {
          value = rawValue ? 'true' : 'false';
        } else {
          value = rawValue != null ? String(rawValue) : '';
        }

        return {
          company_custom_field_id: field.id,
          value,
        };
      })
      .filter((field) => field.value !== '');
  };

  return {
    fields,
    getInitialValues,
    serializeForSubmit,
  };
};
